import React, { useEffect, useRef, useState } from "react";
import { IMedia, LayoutCommonProps } from "../../../types";
import { ArrowUp, ArrowDown } from "react-bootstrap-icons";
import * as Styled from "./Layout2.styled";
import { SeeCard } from "../../media-card/see-card";
import { SidebarCard } from "../../media-card/small-card";
import LayoutModal from "../../media-card/FullScreenModal";
import { LayoutHeader } from "../LayoutHeader";
import { BigCard } from "../../media-card/big-card";
import { startVerticalScroll, stopVerticalScroll } from "../../../libs/utils";
import RatingToast from "../RatingToast";

export const Layout2: React.FC<LayoutCommonProps> = ({
    mediaItems,
    mediaRating,
    onWatchClick,
    onRatingSuccess,
    isSignedIn,
    sessionToken,
    activePlans,
    hasFullAccess,
    freeTrialPlanIds,
    playlistTitle,
    enableCompetitionMode,
    enableRatingControls,
    refreshMediaRatings,
}) => {
    const rightScrollRef = useRef<HTMLDivElement>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mainMedia, setMainMedia] = useState<IMedia | undefined>(undefined);
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [showToast, setShowToast] = useState(false);

    const handleRatingSuccessLocal = () => {
        setShowToast(true);
        onRatingSuccess();
        refreshMediaRatings?.();
    };

    useEffect(() => {
        if (mediaItems.length && !mainMedia) {
            const firstMedia = mediaItems[0];
            setMainMedia(firstMedia);
            setSelectedCardId(firstMedia.id);
        }
    }, [mediaItems, mainMedia]);

    const handleSeeAllClick = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const onCardClick = (item: IMedia) => {
        setMainMedia(item);
        setSelectedCardId(item.id);
    };

    if (!mediaItems.length || !mainMedia) return null;

    const remainingMedia = mediaItems;

    return (
        <>
            <Styled.FullHeightWrapper>
                <LayoutHeader
                    title={playlistTitle}
                    itemCount={mediaItems?.length}
                    onSeeAll={handleSeeAllClick}
                    padding="20px 10px 20px 0px"
                    marginTop="0px"
                />
                <Styled.GridWrapper>
                    {/* Left: Large MediaCard */}
                    <Styled.LeftContainer>
                        <div style={{ position: "relative" }}>
                            <BigCard
                                mediaItem={mainMedia}
                                mediaRating={
                                    mediaRating.find(
                                        (m) => m.mediaId === mainMedia.mediaId
                                    ) || 0
                                }
                                enableRatingControls={enableRatingControls}
                                isSignedIn={isSignedIn}
                                hasEligiblePlan={
                                    isSignedIn &&
                                    (!!mainMedia.planDetails?.length
                                        ? !!activePlans.filter((plan) =>
                                              mainMedia.planDetails?.some(
                                                  (pd) => pd?.id === plan.id
                                              )
                                          ).length ||
                                          freeTrialPlanIds.includes(
                                              mainMedia.planDetails[0]?.id
                                          ) ||
                                          hasFullAccess
                                        : true)
                                }
                                onWatchClick={onWatchClick}
                                onRatingSuccess={handleRatingSuccessLocal}
                                sessionToken={sessionToken}
                                onlyShowWatchButton={false}
                                enableCompetitionMode={enableCompetitionMode}
                            />
                            <RatingToast
                                show={showToast}
                                onClose={() => setShowToast(false)}
                            />
                        </div>
                    </Styled.LeftContainer>

                    {/* Right: Scrollable Media List */}
                    <Styled.RightContainer>
                        <Styled.ScrollableList
                            ref={rightScrollRef}
                            scrollable={remainingMedia.length > 4}
                        >
                            {remainingMedia.slice(0, 6).map((item) => (
                                <div
                                    key={item.id}
                                    onMouseEnter={() => {
                                        setMainMedia(item);
                                    }}
                                    onMouseLeave={() => {
                                        setMainMedia(
                                            mediaItems.find(
                                                (m) => m.id === selectedCardId
                                            ) || mainMedia
                                        );
                                    }}
                                    style={{ paddingBottom: "10px" }}
                                >
                                    <SidebarCard
                                        mediaItem={item}
                                        cardClick={() => onCardClick(item)}
                                        isSelected={item.id === selectedCardId}
                                        mediaRating={
                                            mediaRating.find(
                                                (m) =>
                                                    m.mediaId === item.mediaId
                                            )?.rating || 0
                                        }
                                        enableRatingControls={enableRatingControls}
                                        isSignedIn={isSignedIn}
                                        hasEligiblePlan={
                                            isSignedIn &&
                                            (!!item.planDetails?.length
                                                ? !!activePlans.filter((plan) =>
                                                      item.planDetails?.some(
                                                          (pd) =>
                                                              pd?.id === plan.id
                                                      )
                                                  ).length ||
                                                  freeTrialPlanIds.includes(
                                                      item.planDetails[0]?.id
                                                  ) ||
                                                  hasFullAccess
                                                : true)
                                        }
                                        onWatchClick={onWatchClick}
                                        onRatingSuccess={onRatingSuccess}
                                        sessionToken={sessionToken}
                                        autoWidth={true}
                                    />
                                </div>
                            ))}

                            {/* SeeCard at end */}
                            {remainingMedia.length > 6 && (
                                <div onClick={handleSeeAllClick}>
                                    <SeeCard width="auto" height="125px" />
                                </div>
                            )}
                        </Styled.ScrollableList>

                        {remainingMedia.length > 4 && (
                            <Styled.ScrollButtonWrapper>
                                <Styled.ScrollButton
                                    onMouseDown={() =>
                                        startVerticalScroll(
                                            rightScrollRef,
                                            "up",
                                            scrollIntervalRef
                                        )
                                    }
                                    onMouseUp={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    onMouseLeave={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    title="Scroll Up"
                                >
                                    <ArrowUp size={30} />
                                </Styled.ScrollButton>

                                <Styled.ScrollButton
                                    onMouseDown={() =>
                                        startVerticalScroll(
                                            rightScrollRef,
                                            "down",
                                            scrollIntervalRef
                                        )
                                    }
                                    onMouseUp={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    onMouseLeave={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    title="Scroll Down"
                                >
                                    <ArrowDown size={30} />
                                </Styled.ScrollButton>
                            </Styled.ScrollButtonWrapper>
                        )}
                    </Styled.RightContainer>
                </Styled.GridWrapper>
            </Styled.FullHeightWrapper>

            {/* Modal with Lazy Loaded Cards */}
            <LayoutModal
                title={playlistTitle}
                show={isModalOpen}
                onHide={handleCloseModal}
                remainingMedia={remainingMedia}
                onCardClick={onCardClick}
                isSignedIn={isSignedIn}
                activePlans={activePlans}
                hasFullAccess={hasFullAccess}
                freeTrialPlanIds={freeTrialPlanIds}
                mediaRating={mediaRating}
                onWatchClick={onWatchClick}
                onRatingSuccess={onRatingSuccess}
                sessionToken={sessionToken}
            />
        </>
    );
};
